import { useMemo, useState, type FC } from 'react';
import type { OptionalExchangeRatesCurrentRequest } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type { AuctionDetail } from 'src/apis/monolith/types';
import { Card } from 'src/components/data-display/card';
import { DataItems, DataList } from 'src/components/data-display/data-list';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { Markdown } from 'src/components/data-display/markdown';
import { OperatorLogo } from 'src/components/domain-specifics/operator-logo';
import { Alert } from 'src/components/feedback/alert';
import { Spacer } from 'src/components/layout/spacer';
import { ExpandableText } from 'src/components/text/expandable-text';
import { Heading } from 'src/components/text/heading';
import { AuctionFees } from 'src/pages/transport/auctions/components/auction-fees';
import { PriceUnitButton } from 'src/pages/transport/auctions/components/price-unit-button';
import {
  convertPricesToEURMWh,
  type PricesInEURMWh,
} from 'src/pages/transport/auctions/details/helper';
import { gasTypeLabels } from 'src/pages/transport/secondary-trading/trade/network-point-card';

type AuctionDetailsProps = {
  auction: AuctionDetail;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

export const AuctionDetailsFromTable: FC<AuctionDetailsProps> = ({
  auction,
  exchangeRates,
}) => {
  // null means that the prices could not be converted to EUR/MWh, so we don't offer the EUR/MWh option
  const precalculatedPricesInEURMWh = useMemo(
    () =>
      convertPricesToEURMWh({
        auction,
        exchangeRates,
      }),
    [auction, exchangeRates]
  );
  // null means that the user has not selected the EUR/MWh option, so we show the original unit
  const [pricesInEURMWh, setPricesInEURMWh] = useState<PricesInEURMWh>(null);

  return (
    <div
      className={`
        grid grid-cols-1 items-start gap-md
        lg:grid-cols-2 lg:gap-4xl
      `}
    >
      <div
        className={`
          grid items-center gap-md
          lg:gap-xl
        `}
      >
        <DataList>
          <DataItems>
            <span>Network Point ID</span>
            <span>{auction.networkPoint.identifier}</span>
            <span>Network Point EIC</span>
            <span>
              {auction.networkPoint.acerEic ?? <EmptyValue label="None" />}
            </span>
            <span>Type of Gas</span>
            <span>{gasTypeLabels[auction.networkPoint.gasType0]}</span>
          </DataItems>
        </DataList>

        {(auction.balancingCharge0 || auction.balancingCharge1) && (
          <>
            <Spacer />
            <Alert type="warning">
              The starting price does not include the neutrality charge.
            </Alert>
            <Spacer />
          </>
        )}

        <div className="grid justify-start">
          <PriceUnitButton
            pricesInEURMWh={pricesInEURMWh}
            precalculatedPricesInEURMWh={precalculatedPricesInEURMWh}
            setPricesInEURMWh={setPricesInEURMWh}
          />
        </div>

        <DataList data-testid="auction-fees-embedded-form">
          <AuctionFees auction={auction} pricesInEURMWh={pricesInEURMWh} />
        </DataList>
      </div>

      <div className="grid items-center gap-xl">
        <div
          className={`
            grid grid-flow-col items-center justify-center
            lg:justify-end
          `}
        >
          <OperatorLogo
            src={auction.networkPoint.tso0LogoSmallUrl}
            name={auction.networkPoint.tso0Name}
          />

          {auction.networkPoint.bundled && (
            <OperatorLogo
              src={auction.networkPoint.tso1LogoSmallUrl}
              name={auction.networkPoint.tso1Name}
            />
          )}
        </div>

        {(auction.notes0 || auction.notes1) && (
          <div className="grid items-center gap-md">
            {auction.notes0 && (
              <Card mode="highlight" data-testid="tso-notice-0">
                <Heading mode="title" size="md">
                  Notice from {auction.networkPoint.tso0Name}
                </Heading>
                <ExpandableText lines={3}>
                  <Markdown content={auction.notes0.join('<br/>')} />
                </ExpandableText>
              </Card>
            )}
            {auction.notes1 && (
              <Card mode="highlight" data-testid="tso-notice-1">
                <Heading mode="title" size="md">
                  Notice from {auction.networkPoint.tso1Name}
                </Heading>
                <ExpandableText lines={3}>
                  <Markdown content={auction.notes1.join('<br/>')} />
                </ExpandableText>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
